import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import apiClient from "../api/axios";
import { handleApiError } from "../api/handleApiError";
import { SubOrganizations } from "../models/SubOrganization";
import { Assets } from "../models/Asset";
import { Organization } from "../models/Organization";
import alertService from "../components/alert/alertService";
import { useTranslation } from "react-i18next";
import { common, organization as organizationI18n } from "../i18n";
import { useAuth } from "./UserContext";
import SsoService from "../service/sso.service";
import { IS_SSO_ENABLED, STORAGE_KEYS } from "../constants";
import { isOnboardingCompletedCached, cacheOnboardingCompleted } from "../utils/onboardingCache";
import secureLocalStorage from "../utils/secureLocalStorage";
import { User } from "../models/User";

/** "deciding" until the organisation and its answer are both in hand; "error" is terminal for this mount. */
export type OnboardingGateState = "deciding" | "pending" | "completed" | "error";

const ORG_TYPE_BORROWER = "borrower";

export interface OrganizationState {
  organization?: Organization;
  subOrganizations?: SubOrganizations[];
  assets?: Assets[];
  filteredAssets?: Assets[];
  selectedSubOrganization?: string;
  setSelectedSubOrganization: (id: string | undefined) => void;
  addAsset: (
    organization: string,
    emissionGroup: string,
    subOrganization: string,
    assetType: string,
    assetName: string,
    assetStatus: string,
    siteRef: string
  ) => void;
  editAsset: (
    id: string,
    subOrganization: string,
    assetType: string,
    assetName: string,
    assetStatus: string,
    site: string
  ) => void;
  deleteAsset: (_id: string, subOrganization: string) => void;
  setSubOrganizations: (data: SubOrganizations[]) => void;
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  selectedSite: string | undefined;
  setSelectedSite: Dispatch<SetStateAction<string | undefined>>;
  setOrganization: Dispatch<SetStateAction<Organization | undefined>>;
  /** cat15 persona of the current org (from GET /v2/organizations orgType). */
  isCat15Borrower: () => boolean;
  /** Whether THIS organisation still needs the cat15 business profile. */
  onboardingStatus: OnboardingGateState;
}

export const OrganizationContext = createContext<OrganizationState | undefined>(
  undefined
);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation([common, organizationI18n], {
    nsMode: "fallback",
  });

  const { user } = useAuth();

  const [organization, setOrganization] = useState<Organization>();
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingGateState>("deciding");
  const [subOrganizations, setSubOrganizations] =
    useState<SubOrganizations[]>();

  const [assets, setAssets] = useState<Assets[]>();

  const [selectedSubOrganization, setSelectedSubOrganization] =
    useState<string>();

  const [selectedSite, setSelectedSite] = useState<string | undefined>(
    undefined
  );

  const filteredAssets = useMemo(() => {
    if (selectedSubOrganization === undefined) {
      return assets;
    }

    const subOrg = subOrganizations?.find(
      (s) => s.id === selectedSubOrganization
    );

    let filterItem = assets?.filter(
      (item) => item.suborganizationName === subOrg!.name
    );

    if (selectedSite !== undefined) {
      const site = subOrg?.sites.find((s) => s.id.toString() === selectedSite);

      filterItem = filterItem?.filter((item) => item.siteName === site!.name);
    }

    return filterItem;
  }, [selectedSubOrganization, assets, selectedSite]);

  const [openModal, setOpenModal] = useState<boolean>(false);

  const getOrganization = async () => {
    try {
      const response = await apiClient.get("/v2/organizations");
      if (response.status === 200 && response.data?.results?.organization) {
        setOrganization(response.data.results.organization);
      } else {
        // A 200 carrying no organisation leaves nothing to ask about, so terminate rather than spin on "deciding".
        setOnboardingStatus("error");
      }
    } catch (error) {
      // Without the organisation there is no question to ask, so the gate has to terminate rather than wait forever.
      setOnboardingStatus("error");
      try { handleApiError(error, {}, { messageKey: "error-get-organization", titleKey: "Error-Message" }); } catch { /* alert already shown — prevent unhandled rejection */ }
    }
  };

  const getSubOrganizations = async () => {
    try {
      const response = await apiClient.get("/v2/suborganizations");
      if (response.status === 200) {
        setSubOrganizations(response.data.results.suborganizations);
      }
    } catch (error) {
      try { handleApiError(error, {}, { messageKey: "error-get-sub-organization", titleKey: "Error-Message" }); } catch { /* alert already shown — prevent unhandled rejection */ }
    }
  };

  const getAssets = async () => {
    try {
      const response = await apiClient.get("/v1/assets");
      if (response.status === 200) {
        setAssets(response.data.results.assets);
      }
    } catch (error) {
      try { handleApiError(error, {}, { messageKey: "error-get-assets", titleKey: "Error-Message" }); } catch { /* alert already shown — prevent unhandled rejection */ }
    }
  };

  const addAsset = async (
    organization: string,
    emissionGroup: string,
    subOrganization: string,
    assetType: string,
    assetName: string,
    assetStatus: string,
    siteRef: string
  ) => {
    try {
      await apiClient.post("/v1/assets", {
        name: assetName,
        assetType: assetType,
        emissionGroup: emissionGroup,
        suborganization: subOrganization,
        assetStatus: assetStatus,
        siteRef: siteRef,
      });
      alertService.mixin(t("Create-Asset-Success"));
      getAssets();
    } catch (error: any) {
      try { handleError(error); } catch { /* alert already shown — prevent unhandled rejection */ }
    }
  };

  const handleError = (error: unknown) =>
    handleApiError(
      error,
      {
        USERS_RETRIEVAL_PROCESS_FAILED: "failed_to_retrieve_users_in_organization",
        ASSET_ALREADY_EXISTS: "asset-already-exists",
      },
      { messageKey: "error-create-assets", titleKey: "Error-Message" },
    );

  const editAsset = async (
    id: string,
    subOrganization: string,
    assetType: string,
    assetName: string,
    assetStatus: string,
    siteRef: string
  ) => {
    try {
      await apiClient.post("/v1/assets", {
        _id: id,
        name: assetName,
        assetType: assetType,
        suborganization: subOrganization,
        assetStatus: assetStatus,
        siteRef: siteRef,
      });
      alertService.mixin(t("Edit-Asset-Success"));
      getAssets();
    } catch (error: any) {
      try { handleError(error); } catch { /* alert already shown — prevent unhandled rejection */ }
    }
  };

  const deleteAsset = async (_id: string, subOrganization: string) => {
    try {
      await apiClient.post("/v1/assets", {
        _id: _id,
        suborganization: subOrganization,
        isDelete: true,
      });
      alertService.mixin(t("Delete-Asset-Success"));
      getAssets();
    } catch (error) {
      try { handleError(error); } catch { /* alert already shown — prevent unhandled rejection */ }
    }
  };

  // cat15 persona — orgType comes from GET /v2/organizations; anything other than
  // "borrower" (incl. non-cat15 orgs) is treated as lender.
  const isCat15Borrower = () => organization?.orgType === ORG_TYPE_BORROWER;

  const organizationRef = organization?._id;
  const orgType = organization?.orgType;
  const accessToken = user?.accessToken;

  // Not handleApiError — it alerts, and this probe runs on every page load.
  useEffect(() => {
    // Re-decide from scratch, or the previous organisation's answer stays on screen while this one is in flight.
    setOnboardingStatus("deciding");
    if (!organizationRef) return;
    if (!IS_SSO_ENABLED || orgType !== ORG_TYPE_BORROWER) {
      setOnboardingStatus("completed");
      return;
    }
    if (isOnboardingCompletedCached(organizationRef)) {
      setOnboardingStatus("completed");
      return;
    }
    // Read at call time: a silent refresh writes the rotated token to storage only, leaving React state holding a dead one.
    const token = secureLocalStorage.getJSON<User>(STORAGE_KEYS.USER)?.accessToken ?? accessToken;
    if (!token) return;

    let active = true;
    SsoService.getOnboardingStatus(token, organizationRef)
      .then((result) => {
        if (!active) return;
        if (result.onboarding_status === "completed") cacheOnboardingCompleted(organizationRef);
        setOnboardingStatus(result.onboarding_status);
      })
      .catch(() => {
        if (!active) return;
        // eslint-disable-next-line no-console
        console.warn("[onboarding] status unavailable — the wizard will not be offered for this organisation");
        setOnboardingStatus("error");
      });

    return () => {
      active = false;
    };
  }, [organizationRef, orgType, accessToken]);

  const context = {
    organization,
    isCat15Borrower,
    onboardingStatus,
    subOrganizations,
    assets,
    filteredAssets,
    selectedSubOrganization,
    setSelectedSubOrganization,
    addAsset,
    editAsset,
    deleteAsset,
    setSubOrganizations,
    openModal,
    setOpenModal,
    selectedSite,
    setSelectedSite,
    setOrganization,
  };

  useEffect(() => {
    getOrganization();
    getSubOrganizations();
    getAssets();
  }, []);

  return (
    <OrganizationContext.Provider value={context}>
      {children}
    </OrganizationContext.Provider>
  );
}
